/**
 * useCourseEditing — orchestration hook for course detail editing.
 *
 * Encapsulates all state, derived data, and handlers for editing a course
 * (modules, lessons, drag-and-drop, inline editing, sheet modals, deletes,
 * subject associations). Designed to be consumed by both the legacy admin
 * route (`/admin/courses/:id`) and the new NextGen coursework editor
 * (`/nextgen/coursework/:id?edit=1`) without duplication.
 *
 * Pure extraction from src/components/admin/CourseDetail.tsx — no behavior
 * change vs the original component.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { logger } from '@/lib/logger';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { useAdminCourseDetail, adminCourseKeys } from '@/hooks/useAdminCourses';
import { parseJsonArray } from '@/types/database-mappings';
import type { AppCourse, AppModule, AppLesson } from '@/types/database-mappings';

// react-beautiful-dnd / @hello-pangea/dnd DropResult shape (loose to avoid
// pulling a peer-dep import here; matches what the consumer passes through).
export interface CourseDragEndResult {
  source: { droppableId: string; index: number };
  destination: { droppableId: string; index: number } | null;
  type: string;
  // Other dnd fields are unused here.
  [key: string]: unknown;
}

export interface UseCourseEditingResult {
  // ---- Query state ----
  course: AppCourse | null;
  modules: AppModule[];
  isLoading: boolean;
  error: Error | null;
  refetch: ReturnType<typeof useAdminCourseDetail>['refetch'];
  reloading: boolean;

  // ---- Subject state ----
  selectedSubjectIds: string[];
  setSelectedSubjectIds: React.Dispatch<React.SetStateAction<string[]>>;

  // ---- Inline edit state + refs ----
  inlineEditingModuleId: string | null;
  inlineEditingLessonId: string | null;
  newModuleInputRef: React.RefObject<HTMLInputElement>;
  newLessonInputRefs: React.MutableRefObject<{ [key: string]: HTMLInputElement | null }>;

  // ---- Sheet / modal state ----
  isCourseModalOpen: boolean;
  setIsCourseModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isModuleModalOpen: boolean;
  setIsModuleModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editingModuleId: string | null;
  setEditingModuleId: React.Dispatch<React.SetStateAction<string | null>>;
  isLessonModalOpen: boolean;
  setIsLessonModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editingLessonId: string | null;
  setEditingLessonId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedModule: string | null;
  setSelectedModule: React.Dispatch<React.SetStateAction<string | null>>;
  updateLoading: boolean;

  // ---- Delete dialog state ----
  deleteModuleId: string | null;
  deleteModuleTitle: string;
  deleteModuleLoading: boolean;
  setDeleteModuleId: React.Dispatch<React.SetStateAction<string | null>>;
  setDeleteModuleTitle: React.Dispatch<React.SetStateAction<string>>;
  deleteLessonId: string | null;
  deleteLessonTitle: string;
  deleteLessonLoading: boolean;
  setDeleteLessonId: React.Dispatch<React.SetStateAction<string | null>>;
  setDeleteLessonTitle: React.Dispatch<React.SetStateAction<string>>;

  // ---- Handlers ----
  handleRefresh: () => Promise<void>;
  handleAddModuleInline: () => void;
  handleModuleTitleChange: (moduleId: string, title: string) => void;
  handleSaveModuleInline: (moduleId: string, title: string) => Promise<void>;
  handleCancelModuleInline: (moduleId: string) => void;
  handleToggleModuleExpand: (moduleId: string) => void;
  handleStartEditModule: (moduleId: string) => void;
  handleAddLessonInline: (moduleId: string) => void;
  handleLessonTitleChange: (moduleIndex: number, lessonIndex: number, title: string) => void;
  handleSaveLessonInline: (lessonId: string, title: string) => Promise<void>;
  handleCancelLessonInline: (lessonId: string) => void;
  handleDragEnd: (result: CourseDragEndResult) => Promise<void>;
  handleDeleteModule: () => Promise<void>;
  handleDeleteLesson: () => Promise<void>;
  handleUpdateCourse: (updatedCourse: AppCourse) => Promise<void>;
  handleSheetSaveSuccess: () => void;
}

export function useCourseEditing(
  courseId: string | undefined,
  options: { enabled?: boolean } = {}
): UseCourseEditingResult {
  const queryClient = useQueryClient();
  const { enabled = true } = options;

  // Fetch course detail via React Query (gated by `enabled` so non-admins
  // / non-edit consumers don't fire the admin query unnecessarily).
  const { data: courseData, isLoading, error: queryError, refetch } = useAdminCourseDetail(
    courseId,
    { enabled }
  );
  const [reloading, setReloading] = useState(false);

  // Local modules state for drag-and-drop + inline editing (derived from query data)
  const [localModules, setLocalModules] = useState<AppModule[] | null>(null);

  // Resolve modules: use local state if set, otherwise from query
  const modules: AppModule[] = localModules ?? courseData?.modules ?? [];

  // (Removed dead prevQueryModulesRef block — the ref was written but never
  // read, and the surrounding write was a render-phase mutation. Closes the
  // first half of code-reviewer finding wYVPOEv4r9Q5f5HNTq7z.)

  // Subject IDs state — initialized from query data on first mount, then
  // kept in sync via a useEffect rather than a render-phase setState.
  const [selectedSubjectIds, setSelectedSubjectIds] = useState<string[]>(courseData?.subjectIds ?? []);

  // Hydrate from query data when it loads after mount. Keyed on the
  // serialized id list so we only fire when the actual subject set changes,
  // not on every shallow array re-creation. The previous render-phase
  // `if (...) setSelectedSubjectIds(...)` would silently re-add subjects a
  // user had intentionally cleared on the next refetch — fixed here by
  // gating on a "first hydration" semantic via the courseData reference.
  const hasHydratedSubjectsRef = useRef(false);
  const querySubjectsJson = courseData?.subjectIds ? JSON.stringify(courseData.subjectIds) : '';
  useEffect(() => {
    if (!courseData?.subjectIds) return;
    // Only auto-hydrate ONCE: when query data first arrives and we haven't
    // hydrated yet. After that, the user owns the state — no auto-resync.
    if (!hasHydratedSubjectsRef.current && courseData.subjectIds.length > 0) {
      setSelectedSubjectIds(courseData.subjectIds);
      hasHydratedSubjectsRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [querySubjectsJson]);

  // Modal states
  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
  const [editingModuleId, setEditingModuleId] = useState<string | null>(null);
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState<string | null>(null);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  // Inline editing states
  const [inlineEditingModuleId, setInlineEditingModuleId] = useState<string | null>(null);
  const [inlineEditingLessonId, setInlineEditingLessonId] = useState<string | null>(null);
  const newModuleInputRef = useRef<HTMLInputElement>(null);
  const newLessonInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // Delete confirmation states
  const [deleteModuleId, setDeleteModuleId] = useState<string | null>(null);
  const [deleteModuleTitle, setDeleteModuleTitle] = useState('');
  const [deleteModuleLoading, setDeleteModuleLoading] = useState(false);
  const [deleteLessonId, setDeleteLessonId] = useState<string | null>(null);
  const [deleteLessonTitle, setDeleteLessonTitle] = useState('');
  const [deleteLessonLoading, setDeleteLessonLoading] = useState(false);

  const course: AppCourse | null = courseData?.course ?? null;

  // Get a mutable copy of modules for local edits
  const getModules = useCallback((): AppModule[] => {
    return localModules ?? [...(courseData?.modules ?? [])].map(m => ({
      ...m,
      lessons: m.lessons ? [...m.lessons] : [],
    }));
  }, [localModules, courseData?.modules]);

  const handleRefresh = useCallback(async () => {
    setReloading(true);
    setLocalModules(null);
    await refetch();
    setReloading(false);
  }, [refetch]);

  // ---- Module inline editing ----
  const handleAddModuleInline = useCallback(() => {
    const tempId = `temp-${Date.now()}`;
    const currentModules = getModules();
    const newModule: AppModule = {
      id: tempId,
      tempId,
      title: '',
      course_id: courseId!,
      position: currentModules.length > 0 ? Math.max(...currentModules.map(m => m.position)) + 1 : 1,
      created_at: new Date().toISOString(),
      created_by: null,
      description: null,
      updated_at: null,
      lessons: [],
      isExpanded: true,
      isNew: true,
    };
    setLocalModules([...currentModules, newModule]);
    setInlineEditingModuleId(tempId);
    setTimeout(() => newModuleInputRef.current?.focus(), 0);
  }, [courseId, getModules]);

  const handleModuleTitleChange = useCallback((moduleId: string, title: string) => {
    const updated = getModules().map(m => m.id === moduleId ? { ...m, title } : m);
    setLocalModules(updated);
  }, [getModules]);

  const handleSaveModuleInline = useCallback(async (moduleId: string, title: string) => {
    if (!title.trim() || !courseId) return;
    const currentModules = getModules();
    const mod = currentModules.find(m => m.id === moduleId);
    if (!mod) return;

    try {
      if (mod.isNew) {
        const { data, error } = await supabase
          .from('modules')
          .insert({ title: title.trim(), course_id: courseId, position: mod.position })
          .select('*')
          .single();
        if (error) throw error;
        setLocalModules(currentModules.map(m => m.id === moduleId ? { ...data, lessons: [], isExpanded: true } : m));
        toast({ title: 'Module created', description: 'Module added successfully.' });
      } else {
        const { error } = await supabase.from('modules').update({ title: title.trim() }).eq('id', moduleId);
        if (error) throw error;
        setLocalModules(currentModules.map(m => m.id === moduleId ? { ...m, title: title.trim() } : m));
        toast({ title: 'Module updated', description: 'Module title updated successfully.' });
      }
    } catch (err: unknown) {
      logger.error('Error saving module', err instanceof Error ? err : undefined);
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed to save module.', variant: 'destructive' });
    } finally {
      setInlineEditingModuleId(null);
    }
  }, [courseId, getModules]);

  const handleCancelModuleInline = useCallback((moduleId: string) => {
    const currentModules = getModules();
    const mod = currentModules.find(m => m.id === moduleId);
    if (mod?.isNew) {
      setLocalModules(currentModules.filter(m => m.id !== moduleId));
    }
    setInlineEditingModuleId(null);
  }, [getModules]);

  const handleToggleModuleExpand = useCallback((moduleId: string) => {
    const updated = getModules().map(m => m.id === moduleId ? { ...m, isExpanded: !m.isExpanded } : m);
    setLocalModules(updated);
  }, [getModules]);

  const handleStartEditModule = useCallback((moduleId: string) => {
    setInlineEditingModuleId(moduleId);
    setTimeout(() => newModuleInputRef.current?.focus(), 0);
  }, []);

  // ---- Lesson inline editing ----
  const handleAddLessonInline = useCallback((moduleId: string) => {
    const tempId = `temp-${Date.now()}`;
    const currentModules = getModules();
    const moduleIndex = currentModules.findIndex(m => m.id === moduleId);
    if (moduleIndex === -1) return;

    const newLesson: AppLesson = {
      id: tempId,
      tempId,
      title: '',
      module_id: moduleId,
      status: 'Draft',
      position: currentModules[moduleIndex].lessons?.length || 0,
      created_at: new Date().toISOString(),
      created_by: null,
      updated_at: null,
      content: null,
      video_id: null,
      attachments: [],
      isNew: true,
    };

    const updated = [...currentModules];
    updated[moduleIndex] = { ...updated[moduleIndex], lessons: [...(updated[moduleIndex].lessons || []), newLesson] };
    setLocalModules(updated);
    setInlineEditingLessonId(tempId);
    setTimeout(() => newLessonInputRefs.current[tempId]?.focus(), 0);
  }, [getModules]);

  const handleLessonTitleChange = useCallback((moduleIndex: number, lessonIndex: number, title: string) => {
    const updated = getModules();
    if (updated[moduleIndex]?.lessons?.[lessonIndex]) {
      updated[moduleIndex].lessons![lessonIndex] = { ...updated[moduleIndex].lessons![lessonIndex], title };
      setLocalModules([...updated]);
    }
  }, [getModules]);

  const handleSaveLessonInline = useCallback(async (lessonId: string, title: string) => {
    if (!title.trim()) return;
    const currentModules = getModules();
    let moduleIndex = -1, lessonIndex = -1;
    currentModules.forEach((m, mIdx) => {
      const lIdx = m.lessons?.findIndex(l => l.id === lessonId);
      if (lIdx !== undefined && lIdx !== -1) { moduleIndex = mIdx; lessonIndex = lIdx; }
    });
    if (moduleIndex === -1 || lessonIndex === -1) return;

    const lesson = currentModules[moduleIndex].lessons![lessonIndex];
    try {
      if (lesson.isNew) {
        const { data, error } = await supabase
          .from('lessons')
          .insert({ title: title.trim(), module_id: currentModules[moduleIndex].id, position: lesson.position, status: 'Draft', content: '' })
          .select('*')
          .single();
        if (error) throw error;
        currentModules[moduleIndex].lessons![lessonIndex] = {
          ...data,
          attachments: parseJsonArray(data.attachments, []),
          status: data.status || 'Draft',
          created_by: data.created_by,
          content: data.content,
          updated_at: data.updated_at,
          video_id: data.video_id,
        };
        setLocalModules([...currentModules]);
        toast({ title: 'Lesson created', description: 'Lesson added successfully.' });
      } else {
        const { error } = await supabase.from('lessons').update({ title: title.trim() }).eq('id', lessonId);
        if (error) throw error;
        currentModules[moduleIndex].lessons![lessonIndex].title = title.trim();
        setLocalModules([...currentModules]);
        toast({ title: 'Lesson updated', description: 'Lesson title updated successfully.' });
      }
    } catch (err: unknown) {
      logger.error('Error saving lesson', err instanceof Error ? err : undefined);
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed to save lesson.', variant: 'destructive' });
    } finally {
      setInlineEditingLessonId(null);
    }
  }, [getModules]);

  const handleCancelLessonInline = useCallback((lessonId: string) => {
    const currentModules = getModules();
    let moduleIndex = -1, lessonIndex = -1;
    currentModules.forEach((m, mIdx) => {
      const lIdx = m.lessons?.findIndex(l => l.id === lessonId);
      if (lIdx !== undefined && lIdx !== -1) { moduleIndex = mIdx; lessonIndex = lIdx; }
    });
    if (moduleIndex !== -1 && lessonIndex !== -1 && currentModules[moduleIndex].lessons![lessonIndex].isNew) {
      currentModules[moduleIndex].lessons = currentModules[moduleIndex].lessons!.filter(l => l.id !== lessonId);
      setLocalModules([...currentModules]);
    }
    setInlineEditingLessonId(null);
  }, [getModules]);

  // ---- Drag and drop ----
  const handleDragEnd = useCallback(async (result: CourseDragEndResult) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const currentModules = getModules();

    if (type === 'MODULE') {
      const newModules = Array.from(currentModules);
      const [moved] = newModules.splice(source.index, 1);
      newModules.splice(destination.index, 0, moved);
      const updated = newModules.map((m, i) => ({ ...m, position: i + 1 }));
      setLocalModules(updated);
      try {
        for (const m of updated) {
          await supabase.from('modules').update({ position: m.position }).eq('id', m.id);
        }
      } catch (err) {
        logger.error('Error updating module positions', err instanceof Error ? err : undefined);
      }
    } else if (type === 'LESSON') {
      const sourceModule = currentModules.find(m => m.id === source.droppableId);
      if (!sourceModule?.lessons) return;
      const sourceLessons = Array.from(sourceModule.lessons);
      const [movedLesson] = sourceLessons.splice(source.index, 1);

      if (source.droppableId === destination.droppableId) {
        sourceLessons.splice(destination.index, 0, movedLesson);
        const updated = sourceLessons.map((l, i) => ({ ...l, position: i + 1 }));
        sourceModule.lessons = updated;
        setLocalModules([...currentModules]);
        try {
          for (const l of updated) {
            await supabase.from('lessons').update({ position: l.position }).eq('id', l.id);
          }
        } catch (err) {
          logger.error('Error updating lesson positions', err instanceof Error ? err : undefined);
        }
      } else {
        const destModule = currentModules.find(m => m.id === destination.droppableId);
        if (!destModule?.lessons) return;
        movedLesson.module_id = destination.droppableId;
        const destLessons = Array.from(destModule.lessons);
        destLessons.splice(destination.index, 0, movedLesson);
        sourceModule.lessons = sourceLessons.map((l, i) => ({ ...l, position: i + 1 }));
        destModule.lessons = destLessons.map((l, i) => ({ ...l, position: i + 1 }));
        setLocalModules([...currentModules]);
        try {
          await supabase.from('lessons').update({ module_id: destination.droppableId, position: destModule.lessons.find(l => l.id === movedLesson.id)?.position }).eq('id', movedLesson.id);
          for (const l of sourceModule.lessons) await supabase.from('lessons').update({ position: l.position }).eq('id', l.id);
          for (const l of destModule.lessons) if (l.id !== movedLesson.id) await supabase.from('lessons').update({ position: l.position }).eq('id', l.id);
        } catch (err) {
          logger.error('Error updating lessons', err instanceof Error ? err : undefined);
        }
      }
    }
  }, [getModules]);

  // ---- Delete handlers ----
  const handleDeleteModule = useCallback(async () => {
    if (!deleteModuleId) return;
    setDeleteModuleLoading(true);
    try {
      await supabase.from('lessons').delete().eq('module_id', deleteModuleId);
      const { error } = await supabase.from('modules').delete().eq('id', deleteModuleId);
      if (error) throw error;
      const updated = getModules().filter(m => m.id !== deleteModuleId);
      setLocalModules(updated);
      toast({ title: 'Module deleted', description: 'The module and its lessons have been deleted.' });
    } catch (err: unknown) {
      logger.error('Error deleting module', err instanceof Error ? err : undefined);
      toast({ title: 'Error', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setDeleteModuleLoading(false);
      setDeleteModuleId(null);
      setDeleteModuleTitle('');
    }
  }, [deleteModuleId, getModules]);

  const handleDeleteLesson = useCallback(async () => {
    if (!deleteLessonId) return;
    setDeleteLessonLoading(true);
    try {
      const { error } = await supabase.from('lessons').delete().eq('id', deleteLessonId);
      if (error) throw error;
      const updated = getModules().map(m => ({ ...m, lessons: m.lessons?.filter(l => l.id !== deleteLessonId) || [] }));
      setLocalModules(updated);
      toast({ title: 'Lesson deleted', description: 'The lesson has been deleted.' });
    } catch (err: unknown) {
      logger.error('Error deleting lesson', err instanceof Error ? err : undefined);
      toast({ title: 'Error', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setDeleteLessonLoading(false);
      setDeleteLessonId(null);
      setDeleteLessonTitle('');
    }
  }, [deleteLessonId, getModules]);

  // ---- Course update ----
  const handleUpdateCourse = useCallback(async (updatedCourse: AppCourse) => {
    if (!courseId) return;
    try {
      setUpdateLoading(true);
      const { error: updateError } = await supabase
        .from('courses')
        .update({
          title: updatedCourse.title,
          status: updatedCourse.status,
          tile_description: updatedCourse.tile_description,
          overview: updatedCourse.overview,
          what_youll_learn: updatedCourse.what_youll_learn,
          days_of_access: updatedCourse.days_of_access,
          is_featured: updatedCourse.is_featured,
        })
        .eq('id', courseId);
      if (updateError) throw updateError;

      // Handle subject associations
      await supabase.from('course_subjects').delete().eq('course_id', courseId);
      if (selectedSubjectIds.length > 0) {
        await supabase.from('course_subjects').insert(
          selectedSubjectIds.map(subjectId => ({ course_id: courseId, subject_id: subjectId }))
        );
      }

      toast({ title: 'Course Saved', description: 'Your changes have been saved successfully' });
      setLocalModules(null);
      queryClient.invalidateQueries({ queryKey: adminCourseKeys.detail(courseId) });
      setIsCourseModalOpen(false);
    } catch (err: unknown) {
      logger.error('Error updating course', err instanceof Error ? err : undefined);
      toast({ title: 'Error', description: `Failed to update course: ${err instanceof Error ? err.message : String(err)}`, variant: 'destructive' });
    } finally {
      setUpdateLoading(false);
    }
  }, [courseId, selectedSubjectIds, queryClient]);

  const handleSheetSaveSuccess = useCallback(() => {
    setIsModuleModalOpen(false);
    setIsLessonModalOpen(false);
    setEditingModuleId(null);
    setEditingLessonId(null);
    setSelectedModule(null);
    setLocalModules(null);
    if (courseId) {
      queryClient.invalidateQueries({ queryKey: adminCourseKeys.detail(courseId) });
    }
  }, [courseId, queryClient]);

  return {
    // Query state
    course,
    modules,
    isLoading,
    error: (queryError as Error | null) ?? null,
    refetch,
    reloading,

    // Subject state
    selectedSubjectIds,
    setSelectedSubjectIds,

    // Inline edit state + refs
    inlineEditingModuleId,
    inlineEditingLessonId,
    newModuleInputRef,
    newLessonInputRefs,

    // Sheet / modal state
    isCourseModalOpen,
    setIsCourseModalOpen,
    isModuleModalOpen,
    setIsModuleModalOpen,
    editingModuleId,
    setEditingModuleId,
    isLessonModalOpen,
    setIsLessonModalOpen,
    editingLessonId,
    setEditingLessonId,
    selectedModule,
    setSelectedModule,
    updateLoading,

    // Delete dialog state
    deleteModuleId,
    deleteModuleTitle,
    deleteModuleLoading,
    setDeleteModuleId,
    setDeleteModuleTitle,
    deleteLessonId,
    deleteLessonTitle,
    deleteLessonLoading,
    setDeleteLessonId,
    setDeleteLessonTitle,

    // Handlers
    handleRefresh,
    handleAddModuleInline,
    handleModuleTitleChange,
    handleSaveModuleInline,
    handleCancelModuleInline,
    handleToggleModuleExpand,
    handleStartEditModule,
    handleAddLessonInline,
    handleLessonTitleChange,
    handleSaveLessonInline,
    handleCancelLessonInline,
    handleDragEnd,
    handleDeleteModule,
    handleDeleteLesson,
    handleUpdateCourse,
    handleSheetSaveSuccess,
  };
}
